// Missions: a goal that measures progress over a fixed set of items (e.g.
// "draw every basic hiragana") read from one of the per-feature progress
// snapshots the XP/achievements system already uses (see xp.ts). Pure and
// side-effect free - no storage, network or React here. Completing a mission
// unlocks the achievement named by `achievementId` (see achievements.ts, which
// imports this module - keep the dependency one-way: missions never imports
// achievements). Missions award no XP of their own.
//
// Adding a mission is a one-entry addition to MISSIONS: pick the snapshot
// `feature`, list the `items`, and give an `isItemDone` predicate for that
// feature's value shape (draw counts: >= 1; learned/known sets: a string
// match). Then add its achievement (with `missionId`) and the EN/PT strings.
import { allKanjiMeanings } from "../consts/allKanjiMeanings";
import { isFiniteNumber } from "./storageValidation";
import { ProgressSnapshots } from "./xp";

export type MissionFeature = keyof ProgressSnapshots;

export type Mission = {
    id: string;
    // English defaults; the UI looks up `missions.<id>.{title,description,cta}`
    // in the common i18n namespace first (see useProgressText).
    title: string;
    description: string;
    // The achievement unlocked on completion - must exist in ACHIEVEMENTS with
    // a matching `missionId` (enforced by a test).
    achievementId: string;
    // Which progress snapshot the items are looked up in.
    feature: MissionFeature;
    // The set of things to finish, in the order they are suggested "next up".
    items: readonly string[];
    // Whether the snapshot's value for one item counts as done. Receives the
    // raw (untrusted - cloud rows are not validated) value.
    isItemDone: (value: unknown) => boolean;
    // i18n key under homePage `missions.units` for "12 / 46 <unit>".
    unit: string;
    // Language-neutral route (localise with useLocalizedPath) to work on it.
    practicePath: string;
    // Corner icon on the mission row marking what kind of mission it is
    // (drawing or listening). Decorative: the title already says what to do.
    kind?: "draw" | "listen";
};

export type MissionProgress = {
    mission: Mission;
    done: number;
    total: number;
    // 0-100, floored so a nearly-finished mission never reads 100% before it
    // is actually complete.
    percent: number;
    completed: boolean;
    // Items not done yet, in the mission's own order.
    remaining: string[];
    // First of `remaining`, or null when complete.
    next: string | null;
};

// A kana/kanji counts as drawn once its draw count is at least 1. Non-numbers,
// NaN/Infinity, negatives and fractions below 1 never count. Note the practice
// pages only record a drawing that was finished with zero mistakes, so "drawn"
// means "drawn without mistakes" (the mission copy says so).
export const drawnAtLeastOnce = (value: unknown): boolean => isFiniteNumber(value) && value >= 1;

// A kana/kanji counts as listened-to once its listen count is at least 1.
// Same acceptance rule as drawnAtLeastOnce, kept as its own named export
// (rather than an alias) since listening and drawing are different user
// actions recorded in different snapshots - a reader of a listen mission
// should not have to follow an import named after drawing.
export const listenedAtLeastOnce = (value: unknown): boolean => isFiniteNumber(value) && value >= 1;

// "All hiragana" / "all katakana" means the 46 basic gojuon kana of each
// script - deliberately NOT everything the Kana Writing Practice page offers.
// That page (hiraganaWritingList / katakanaWritingList) also lists the 25
// dakuten/handakuten forms as separate draw targets (71 Hiragana and 71
// Katakana), so requiring all of those would make the first missions a much
// bigger ask. The basic 46 are
// what a beginner means by "learn hiragana", and a test guarantees each one is
// a drawable target on the practice page, so the mission is always achievable.
const GOJUON_HIRAGANA_ROWS = [
    "あいうえお",
    "かきくけこ",
    "さしすせそ",
    "たちつてと",
    "なにぬねの",
    "はひふへほ",
    "まみむめも",
    "やゆよ",
    "らりるれろ",
    "わをん",
];

const GOJUON_KATAKANA_ROWS = [
    "アイウエオ",
    "カキクケコ",
    "サシスセソ",
    "タチツテト",
    "ナニヌネノ",
    "ハヒフヘホ",
    "マミムメモ",
    "ヤユヨ",
    "ラリルレロ",
    "ワヲン",
];

const rowsToSymbols = (rows: string[]): string[] => rows.join("").split("");

// "All N5 kanji" is every kanji the app tags JLPT level 5 in allKanjiMeanings -
// the same list the Kanji Writing Practice page shows under its N5 tab and
// that the worksheets' N5 filter (kanjiSymbolsForLevel(5)) uses, so the mission
// cannot drift from what a learner can actually draw. Nothing is excluded:
// every one of these kanji has stroke data in public/stroke-data (a test
// guards that, since a kanji that cannot be drawn would make the mission
// impossible to finish). allKanjiMeanings is already part of the eager bundle
// (the blog imports it), so this adds no weight to the header-wide
// achievements code that imports this module.
const N5_KANJI: string[] = (allKanjiMeanings as Array<{ symbol: string; jlptLevel?: number | null }>)
    .filter((entry) => entry.jlptLevel === 5)
    .map((entry) => entry.symbol);

export const MISSIONS: Mission[] = [
    {
        id: "draw-all-hiragana",
        title: "Draw all hiragana",
        description: "Draw each of the 46 basic hiragana once, without mistakes.",
        achievementId: "hiragana-artist",
        feature: "kanaDrawCounts",
        items: rowsToSymbols(GOJUON_HIRAGANA_ROWS),
        isItemDone: drawnAtLeastOnce,
        unit: "characters",
        kind: "draw",
        practicePath: "/kana-writing-practice?script=hiragana",
    },
    {
        id: "draw-all-katakana",
        title: "Draw all katakana",
        description: "Draw each of the 46 basic katakana once, without mistakes.",
        achievementId: "katakana-artist",
        feature: "kanaDrawCounts",
        items: rowsToSymbols(GOJUON_KATAKANA_ROWS),
        isItemDone: drawnAtLeastOnce,
        unit: "characters",
        kind: "draw",
        practicePath: "/kana-writing-practice?script=katakana",
    },
    {
        id: "draw-all-n5-kanji",
        title: "Draw all N5 kanji",
        description: "Draw each N5 kanji once, without mistakes.",
        achievementId: "n5-kanji-artist",
        feature: "kanjiDrawCounts",
        items: N5_KANJI,
        isItemDone: drawnAtLeastOnce,
        unit: "kanji",
        kind: "draw",
        // The Kanji Writing Practice page has no level deep link (it opens on
        // the learner's saved JLPT level, N5 by default), so this is the page.
        practicePath: "/kanji-writing-practice",
    },
    {
        id: "listen-all-hiragana",
        title: "Listen to all hiragana",
        description: "Listen to each of the 46 basic hiragana once.",
        achievementId: "hiragana-listener",
        feature: "kanaListenCounts",
        items: rowsToSymbols(GOJUON_HIRAGANA_ROWS),
        isItemDone: listenedAtLeastOnce,
        unit: "characters",
        kind: "listen",
        practicePath: "/kana-writing-practice?script=hiragana",
    },
    {
        id: "listen-all-katakana",
        title: "Listen to all katakana",
        description: "Listen to each of the 46 basic katakana once.",
        achievementId: "katakana-listener",
        feature: "kanaListenCounts",
        items: rowsToSymbols(GOJUON_KATAKANA_ROWS),
        isItemDone: listenedAtLeastOnce,
        unit: "characters",
        kind: "listen",
        practicePath: "/kana-writing-practice?script=katakana",
    },
    {
        id: "listen-all-n5-kanji",
        title: "Listen to all N5 kanji",
        description: "Listen to each N5 kanji once.",
        achievementId: "n5-kanji-listener",
        feature: "kanjiListenCounts",
        items: N5_KANJI,
        isItemDone: listenedAtLeastOnce,
        unit: "kanji",
        kind: "listen",
        practicePath: "/kanji-writing-practice",
    },
];

export function getMission(id: string, missions: Mission[] = MISSIONS): Mission | undefined {
    return missions.find((mission) => mission.id === id);
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

function progressFor(mission: Mission, snapshots: ProgressSnapshots): MissionProgress {
    const snapshot: unknown = snapshots[mission.feature];
    const values = isRecord(snapshot) ? snapshot : {};
    // Object.keys lists only own properties, so an item can never be
    // satisfied by an inherited one (and there is no reliance on newer
    // built-ins like Object.hasOwn - this runs in the header on every page).
    const doneItems = new Set(Object.keys(values).filter((key) => mission.isItemDone(values[key])));
    const remaining = mission.items.filter((item) => !doneItems.has(item));
    const total = mission.items.length;
    const done = total - remaining.length;
    const completed = total > 0 && remaining.length === 0;
    return {
        mission,
        done,
        total,
        percent: completed ? 100 : Math.floor((done / Math.max(total, 1)) * 100),
        completed,
        remaining,
        next: remaining.length > 0 ? remaining[0] : null,
    };
}

// Progress for every mission, in MISSIONS order. Tolerates missing, malformed
// or hostile snapshot data (treated as "nothing done") and never mutates it.
export function computeMissionProgress(
    snapshots: ProgressSnapshots = {},
    missions: Mission[] = MISSIONS
): MissionProgress[] {
    const safeSnapshots = isRecord(snapshots) ? snapshots : {};
    return missions.map((mission) => progressFor(mission, safeSnapshots));
}

export function isMissionComplete(
    missionId: string,
    snapshots: ProgressSnapshots = {},
    missions: Mission[] = MISSIONS
): boolean {
    const mission = getMission(missionId, missions);
    if (!mission) return false;
    const safeSnapshots = isRecord(snapshots) ? snapshots : {};
    return progressFor(mission, safeSnapshots).completed;
}
